import React from 'react';
import { useForm } from 'react-hook-form';
import Modal from './ui/Modal';
import Button from './ui/Button';
import toast from 'react-hot-toast';

/**
 * Componente: AddBulkSlotsModal
 * Modal para adicionar múltiplos horários com intervalo configurável
 */
const AddBulkSlotsModal = ({ isOpen, onClose, onSubmit }) => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      startDate: today,
      endDate: today,
      startTime: '09:00',
      endTime: '18:00',
      interval: '60',
    },
  });

  const generateSlots = (data) => {
    const slots = [];
    const { startDate, endDate, startTime, endTime, interval } = data;
    
    // Converter datas
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    
    // Iterar sobre cada dia
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const y = currentDate.getFullYear();
      const m = String(currentDate.getMonth() + 1).padStart(2, '0');
      const d = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      
      // Gerar horários para o dia
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      
      let currentMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      const intervalMinutes = parseInt(interval);
      
      while (currentMinutes < endMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const minutes = currentMinutes % 60;
        const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        
        slots.push({
          date: dateStr,
          time: timeStr,
        });
        
        currentMinutes += intervalMinutes;
      }
      
      // Próximo dia
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return slots;
  };

  const onSubmitForm = (data) => {
    const slots = generateSlots(data);
    
    if (slots.length === 0) {
      toast.error('Nenhum horário foi gerado. Verifique os dados.');
      return;
    }
    
    if (slots.length > 200) {
      toast.error('Muitos horários! Limite: 200. Reduza o período ou aumente o intervalo.');
      return;
    }
    
    onSubmit(slots);
    onClose();
  };

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');
  const watchInterval = watch('interval');

  // Preview de quantos slots serão criados
  const previewCount = () => {
    try {
      const data = { 
        startDate: watchStartDate, 
        endDate: watchEndDate, 
        startTime: watchStartTime, 
        endTime: watchEndTime, 
        interval: watchInterval 
      };
      return generateSlots(data).length;
    } catch {
      return 0;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar Horários em Lote">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
        {/* Período */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Início *
            </label>
            <input
              type="date"
              {...register('startDate', { required: 'Data inicial obrigatória' })}
              min={today}
              className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.startDate && (
              <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim *
            </label>
            <input
              type="date"
              {...register('endDate', { required: 'Data final obrigatória' })}
              min={watchStartDate || today}
              className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.endDate && (
              <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
            )}
          </div>
        </div>

        {/* Horários */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horário Início *
            </label>
            <input
              type="time"
              {...register('startTime', { required: 'Horário inicial obrigatório' })}
              className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.startTime && (
              <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horário Fim *
            </label>
            <input
              type="time"
              {...register('endTime', { required: 'Horário final obrigatório' })}
              className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.endTime && (
              <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
            )}
          </div>
        </div>

        {/* Intervalo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Intervalo entre Horários *
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label
              className={`p-3 border-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                watchInterval === '30'
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                {...register('interval')}
                value="30"
                className="sr-only"
              />
              <div className="text-lg font-bold">30 min</div>
              <div className="text-xs text-gray-500">Mais horários disponíveis</div>
            </label>
            
            <label
              className={`p-3 border-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                watchInterval === '60'
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                {...register('interval')}
                value="60"
                className="sr-only"
              />
              <div className="text-lg font-bold">60 min</div>
              <div className="text-xs text-gray-500">1 hora de intervalo</div>
            </label>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Preview:</span> Serão criados aproximadamente{' '}
            <span className="font-bold text-lg">{previewCount()}</span> horários
          </p>
        </div>

        {/* Ações */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            Criar Horários
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddBulkSlotsModal;
